import { useEffect, useState } from "react";
import { k, onExternalChange, readJSON, writeJSON } from "@/lib/storage";
import {
  applyGrade,
  boxFromMastery,
  intervalMs,
  type Grade,
  type SrsRecord,
} from "@/lib/srs";
import type { Mastery } from "@/types/lex";

/**
 * Persistent schedule for every word the learner has answered. Keys match the
 * mastery store — `${day}:${wordIndex}` — so the two line up entry for entry.
 *
 * Same module-level bus as `useMastery`: no Context provider, any write
 * re-renders every subscribed screen.
 */
export type SrsMap = Record<string, SrsRecord>;

const KEY = k("srs.v1");

export function srsKey(day: number, wordIndex: number): string {
  return `${day}:${wordIndex}`;
}

const listeners = new Set<() => void>();
let snapshot: SrsMap = readJSON<SrsMap>(KEY, {});

function commit(next: SrsMap): void {
  snapshot = next;
  writeJSON(KEY, next);
  for (const fn of listeners) fn();
}

// Another tab graded a word: adopt its state rather than overwriting it on our
// next write. Re-read only — writing here would bounce the event back.
onExternalChange(KEY, () => {
  snapshot = readJSON<SrsMap>(KEY, {});
  for (const fn of listeners) fn();
});

export function getRecord(day: number, wordIndex: number): SrsRecord | undefined {
  return snapshot[srsKey(day, wordIndex)];
}

/** Grade one word and persist the new schedule. Returns the stored record. */
export function gradeRecord(
  day: number,
  wordIndex: number,
  grade: Grade,
  now: number
): SrsRecord {
  const key = srsKey(day, wordIndex);
  const record = applyGrade(snapshot[key], grade, now);
  commit({ ...snapshot, [key]: record });
  return record;
}

/**
 * Create (or reset) a schedule from a hand-picked label. Unlike `gradeRecord`
 * this isn't a recall event: `reps` stays put and the word is parked at the
 * box that matches the label.
 */
export function seedRecord(
  day: number,
  wordIndex: number,
  value: Exclude<Mastery, "untouched">,
  now: number,
  dueOffsetMs?: number
): SrsRecord {
  const key = srsKey(day, wordIndex);
  const prev = snapshot[key];
  const box = boxFromMastery(value);
  const record: SrsRecord = {
    box,
    due: now + (dueOffsetMs ?? (box === 0 ? 0 : intervalMs(box))),
    last: prev?.last ?? 0,
    reps: prev?.reps ?? 0,
    lapses: prev?.lapses ?? 0,
  };
  commit({ ...snapshot, [key]: record });
  return record;
}

export function dropRecord(day: number, wordIndex: number): void {
  const key = srsKey(day, wordIndex);
  if (!(key in snapshot)) return;
  const next = { ...snapshot };
  delete next[key];
  commit(next);
}

export interface DueEntry {
  day: number;
  index: number;
  record: SrsRecord;
}

/**
 * Every word whose interval has elapsed, most overdue first. Read
 * imperatively (not through a hook) so the Review screen can freeze a queue
 * for the length of a session instead of rebuilding it under its own feet.
 */
export function dueEntries(now: number): DueEntry[] {
  const out: DueEntry[] = [];
  for (const [key, record] of Object.entries(snapshot)) {
    if (record.due > now) continue;
    const [dayStr, indexStr] = key.split(":");
    const day = Number(dayStr);
    const index = Number(indexStr);
    if (!Number.isFinite(day) || !Number.isFinite(index)) continue;
    out.push({ day, index, record });
  }
  out.sort((a, b) => a.record.due - b.record.due);
  return out;
}

export function dueCountByDay(now: number): Map<number, number> {
  const counts = new Map<number, number>();
  for (const entry of dueEntries(now)) {
    counts.set(entry.day, (counts.get(entry.day) ?? 0) + 1);
  }
  return counts;
}

/** Subscribe a component to schedule changes. */
export function useSrsSnapshot(): SrsMap {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return snapshot;
}

export function allSrsEntries(): SrsMap {
  return snapshot;
}

export function replaceSrs(next: SrsMap): void {
  commit(next);
}

export function resetSrs(): void {
  commit({});
}
